import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Current, LabelGroupLabelList, LabelGroupList, LabelSuggest } from '../../types/common'

import { nowDate, format, endOfLastWeek, startOfLastWeek, endOfDay, yesterDay } from '../../../utils/date'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CurrentState extends Current { }

type DashboardRangeType = "daily" | "weekly" | "custom"
type ReportRangeType = "daily" | "monthly" | "custom"
type SummaryRangeType = "daily" | "monthly" | "custom"

const FORMAT_DAILY = 'YYYY-MM-DD'
const FORMAT_MONTHLY = 'YYYYMM'
const { localStorage, sessionStorage } = window

const getExpirationDate = (): string => {
  const expiration_date = localStorage.getItem("expiration_date")
  if (!expiration_date) {
    return endOfDay().toString()
  }

  return expiration_date
}

const getTargetDate = (): string => {
  const target_date = localStorage.getItem("target_date")
  if (!target_date || new Date(getExpirationDate()) < new Date()) {
    return yesterDay(FORMAT_DAILY)
  }

  return target_date
}

const getTargetMonth = (): string => {
  const target_month = localStorage.getItem("target_month")
  if (!target_month || new Date(getExpirationDate()) < new Date()) {
    return nowDate(FORMAT_MONTHLY)
  }

  return target_month
}

const getTargetStartDate = (): string => {
  const target_start_date = localStorage.getItem("target_start_date")
  if (!target_start_date || new Date(getExpirationDate()) < new Date()) {
    return format(startOfLastWeek(nowDate(FORMAT_DAILY)), FORMAT_DAILY)
  }

  return target_start_date
}

const getTargetEndDate = (): string => {
  const target_end_date = localStorage.getItem("target_end_date")
  if (!target_end_date || new Date(getExpirationDate()) < new Date()) {
    return format(endOfLastWeek(nowDate(FORMAT_DAILY)), FORMAT_DAILY)
  }

  return target_end_date
}

const getDashboardRangeType = (): DashboardRangeType => {
  const dashboard_range_type = localStorage.getItem("dashboard_range_type") as DashboardRangeType
  if (!dashboard_range_type || new Date(getExpirationDate()) < new Date()) {
    return "daily"
  }

  return dashboard_range_type
}

const getReportRangeType = (): ReportRangeType => {
  const report_range_type = localStorage.getItem("report_range_type") as ReportRangeType
  if (!report_range_type || new Date(getExpirationDate()) < new Date()) {
    return "daily"
  }

  return report_range_type
}

const getDashboardSelectTeam = (): string => {
  const dashboard_select_team = sessionStorage.getItem("dashboard_select_team")
  if (!dashboard_select_team || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return dashboard_select_team
}

const getDashboardSelectOccupation = (): string => {
  const dashboard_select_occupation = sessionStorage.getItem("dashboard_select_occupation")
  if (!dashboard_select_occupation || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return dashboard_select_occupation
}

const getDashboardSelectContract = (): string => {
  const dashboard_select_contract = sessionStorage.getItem("dashboard_select_contract")
  if (!dashboard_select_contract || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return dashboard_select_contract
}

const getDashboardSelectPlace = (): string => {
  const dashboard_select_place = sessionStorage.getItem("dashboard_select_place")
  if (!dashboard_select_place || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return dashboard_select_place
}

const getTimelineSelectTeam = (): string => {
  const timeline_select_team = sessionStorage.getItem("timeline_select_team")
  if (!timeline_select_team || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return timeline_select_team
}

const getTimelineSelectOccupation = (): string => {
  const timeline_select_occupation = sessionStorage.getItem("timeline_select_occupation")
  if (!timeline_select_occupation || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return timeline_select_occupation
}

const getTimelineSelectContract = (): string => {
  const timeline_select_contract = sessionStorage.getItem("timeline_select_contract")
  if (!timeline_select_contract || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return timeline_select_contract
}

const getTimelineSelectPlace = (): string => {
  const timeline_select_place = sessionStorage.getItem("timeline_select_place")
  if (!timeline_select_place || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return timeline_select_place
}

const getAnalyzeSelectDate = (): string => {
  const analyze_select_date = sessionStorage.getItem("analyze_select_date")
  if (!analyze_select_date || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return analyze_select_date
}

const getAnalyzeSelectTeam = (): string => {
  const analyze_select_team = sessionStorage.getItem("analyze_select_team")
  if (!analyze_select_team || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return analyze_select_team
}

const getAnalyzeSelectMember = (): string => {
  const analyze_select_member = sessionStorage.getItem("analyze_select_member")
  if (!analyze_select_member || new Date(getExpirationDate()) < new Date()) {
    return ""
  }

  return analyze_select_member
}

const getTaskAnnounce = (): boolean => {
  const task_announce = localStorage.getItem("task_announce")

  return JSON.parse(task_announce || "false")
}

const getTaskTargetDate = (): string => {
  const task_target_date = localStorage.getItem("task_target_date")
  if (!task_target_date || new Date(getExpirationDate()) < new Date()) {
    return nowDate(FORMAT_DAILY)
  }

  return task_target_date
}

const getTaskLabelSuggest = (): LabelSuggest[] => {
  const task_label_suggest = localStorage.getItem("task_label_suggest") || "[]"

  return JSON.parse(task_label_suggest)
}

const getInformationConfirmed = (): number[] => {
  const information_confirmed = localStorage.getItem("information_confirmed") || ""
  if (!information_confirmed) return []
  return information_confirmed.split(",").map((v) => Number(v))
}

const getTaskStatusListView = (): boolean => {
  const task_status_list_view = localStorage.getItem("task_status_list_view")

  return JSON.parse(task_status_list_view || "false")
}

const getSummaryRangeType = (): SummaryRangeType => {
  const summary_range_type = localStorage.getItem("summary_range_type") as SummaryRangeType
  if (!summary_range_type || new Date(getExpirationDate()) < new Date()) {
    return "daily"
  }

  return summary_range_type
}

const getTaskApplication = (): boolean => {
  const task_application = localStorage.getItem("task_application")

  return JSON.parse(task_application || "false")
}

const getSideOpen = (): boolean => {
  const side_open = localStorage.getItem("side_open")

  return JSON.parse(side_open || "false")
}

const getTaskGanttChartDisplayTypeInit = (): string => {
  const task_gantt_chart_display_type_init = localStorage.getItem("task_gantt_chart_display_type_init")

  return task_gantt_chart_display_type_init || "project"
}

const getTaskGanttChartRangeTypeInit = (): string => {
  const task_gantt_chart_range_type_init = localStorage.getItem("task_gantt_chart_range_type_init")

  return task_gantt_chart_range_type_init || "daily"
}

const getSettingHolidays = (): boolean => {
  const setting_holidays = localStorage.getItem("setting_holidays")

  return JSON.parse(setting_holidays || "false")
}

const getTaskSearchQuery = (): string => {
  const task_search_query = localStorage.getItem("task_search_query") || ""

  return task_search_query
}

const getTaskLabelSearchQuery = (): string => {
  const task_label_search_query = localStorage.getItem("task_label_search_query") || ""

  return task_label_search_query
}

const getTaskAutoLabelingSearchQuery = (): string => {
  const task_auto_labeling_search_query = localStorage.getItem("task_auto_labeling_search_query") || ""

  return task_auto_labeling_search_query
}

const getTaskTargetSettingDisplayType = (): string => {
  const task_target_setting_display_type = localStorage.getItem("task_target_setting_display_type") || "all"

  return task_target_setting_display_type
}

const getTaskLabelGroupOrder = (): LabelGroupList[] => {
  const task_label_group_order = localStorage.getItem("task_label_group_order") || "[]"

  return JSON.parse(task_label_group_order)
}

const getTaskLabelSettingCategoryView = (): boolean => {
  const task_label_setting_category_view = localStorage.getItem("task_label_setting_category_view")

  return JSON.parse(task_label_setting_category_view || "false")
}

const getTaskLabelGroupLabelOrder = (): LabelGroupLabelList[] => {
  const task_label_group_label_order = localStorage.getItem("task_label_group_label_order") || "[]"

  return JSON.parse(task_label_group_label_order)
}

const getTaskLabelingBaseWorkTimeView = (): boolean => {
  const task_labeling_base_work_time_view = localStorage.getItem("task_labeling_base_work_time_view")

  return JSON.parse(task_labeling_base_work_time_view || "false")
}

const getTaskLabelingColumnNumber = (): number => {
  const task_labeling_column_number = Number(localStorage.getItem("task_labeling_column_number")) || 4

  return task_labeling_column_number
}

const getTaskLabelingSortType = (): string => {
  const task_labeling_sort_type = localStorage.getItem("task_labeling_sort_type") || "measure"

  return task_labeling_sort_type
}

const getTaskLabelingGuide = (): boolean => {
  const task_labeling_guide = localStorage.getItem("task_labeling_guide")

  return JSON.parse(task_labeling_guide || "false")
}

const getTaskApprovalDisplayType = (): string[] => {
  const task_approval_display_type = localStorage.getItem("task_approval_display_type") || "[]"

  return JSON.parse(task_approval_display_type)
}

const getTaskApprovalStatusDisplayRate = (): boolean => {
  const task_approval_status_display_rate = localStorage.getItem("task_approval_status_display_rate")

  return JSON.parse(task_approval_status_display_rate || "false")
}

const getTaskLabelingDisplayBandChart = (): boolean => {
  const task_labeling_display_band_chart = localStorage.getItem("task_labeling_display_band_chart")

  return JSON.parse(task_labeling_display_band_chart || "false")
}

const getSettingTeamCreateTeam = (): boolean => {
  const setting_team_create_team = localStorage.getItem("setting_team_create_team")

  return JSON.parse(setting_team_create_team || "true")
}

const getSettingTeamOpenTeamIds = (): number[] => {
  const setting_team_open_team_ids = localStorage.getItem("setting_team_open_team_ids") || ""
  if (!setting_team_open_team_ids) return []
  return setting_team_open_team_ids.split(",").map((v) => Number(v))
}

const initialState: CurrentState = {
  expirationDate: getExpirationDate(),
  targetDate: getTargetDate(),
  targetMonth: getTargetMonth(),
  targetStartDate: getTargetStartDate(),
  targetEndDate: getTargetEndDate(),
  dashboardRangeType: getDashboardRangeType(),
  reportRangeType: getReportRangeType(),
  dashboardSelectTeam: getDashboardSelectTeam(),
  dashboardSelectOccupation: getDashboardSelectOccupation(),
  dashboardSelectContract: getDashboardSelectContract(),
  dashboardSelectPlace: getDashboardSelectPlace(),
  timelineSelectTeam: getTimelineSelectTeam(),
  timelineSelectOccupation: getTimelineSelectOccupation(),
  timelineSelectContract: getTimelineSelectContract(),
  timelineSelectPlace: getTimelineSelectPlace(),
  analyzeSelectDate: getAnalyzeSelectDate(),
  analyzeSelectTeam: getAnalyzeSelectTeam(),
  analyzeSelectMember: getAnalyzeSelectMember(),
  taskAnnounce: getTaskAnnounce(),
  taskTargetDate: getTaskTargetDate(),
  taskLabelSuggest: getTaskLabelSuggest(),
  informationConfirmed: getInformationConfirmed(),
  taskStatusListView: getTaskStatusListView(),
  summaryRangeType: getSummaryRangeType(),
  taskApplication: getTaskApplication(),
  sideOpen: getSideOpen(),
  taskGanttChartDisplayTypeInit: getTaskGanttChartDisplayTypeInit(),
  taskGanttChartRangeTypeInit: getTaskGanttChartRangeTypeInit(),
  settingHolidays: getSettingHolidays(),
  taskSearchQuery: getTaskSearchQuery(),
  taskLabelSearchQuery: getTaskLabelSearchQuery(),
  taskAutoLabelingSearchQuery: getTaskAutoLabelingSearchQuery(),
  taskTargetSettingDisplayType: getTaskTargetSettingDisplayType(),
  taskLabelGroupOrder: getTaskLabelGroupOrder(),
  taskLabelSettingCategoryView: getTaskLabelSettingCategoryView(),
  taskLabelGroupLabelOrder: getTaskLabelGroupLabelOrder(),
  taskLabelingBaseWorkTimeView: getTaskLabelingBaseWorkTimeView(),
  taskLabelingColumnNumber: getTaskLabelingColumnNumber(),
  taskLabelingSortType: getTaskLabelingSortType(),
  taskLabelingGuide: getTaskLabelingGuide(),
  taskApprovalDisplayType: getTaskApprovalDisplayType(),
  taskApprovalStatusDisplayRate: getTaskApprovalStatusDisplayRate(),
  taskLabelingDisplayBandChart: getTaskLabelingDisplayBandChart(),
  settingTeamCreateTeam: getSettingTeamCreateTeam(),
  settingTeamOpenTeamIds: getSettingTeamOpenTeamIds(),
}

const setTargetDate = (date: string) => {
  localStorage.setItem("target_date", date)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTargetMonth = (date: string) => {
  localStorage.setItem("target_month", date)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTargetStartDate = (date: string) => {
  localStorage.setItem("target_start_date", date)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTargetEndDate = (date: string) => {
  localStorage.setItem("target_end_date", date)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setDashboardRangeType = (type: DashboardRangeType) => {
  localStorage.setItem("dashboard_range_type", type)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setReportRangeType = (type: ReportRangeType) => {
  localStorage.setItem("report_range_type", type)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setDashboardSelectTeam = (value: string) => {
  sessionStorage.setItem("dashboard_select_team", value)
}

const setDashboardSelectOccupation = (value: string) => {
  sessionStorage.setItem("dashboard_select_occupation", value)
}

const setDashboardSelectContract = (value: string) => {
  sessionStorage.setItem("dashboard_select_contract", value)
}

const setDashboardSelectPlace = (value: string) => {
  sessionStorage.setItem("dashboard_select_place", value)
}

const setTimelineSelectTeam = (value: string) => {
  sessionStorage.setItem("timeline_select_team", value)
}

const setTimelineSelectOccupation = (value: string) => {
  sessionStorage.setItem("timeline_select_occupation", value)
}

const setTimelineSelectContract = (value: string) => {
  sessionStorage.setItem("timeline_select_contract", value)
}

const setTimelineSelectPlace = (value: string) => {
  sessionStorage.setItem("timeline_select_place", value)
}

const setAnalyzeSelectDate = (value: string) => {
  sessionStorage.setItem("analyze_select_date", value)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setAnalyzeSelectTeam = (value: string) => {
  sessionStorage.setItem("analyze_select_team", value)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setAnalyzeSelectMember = (value: string) => {
  sessionStorage.setItem("analyze_select_member", value)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTaskAnnounce = (value: boolean) => {
  localStorage.setItem("task_announce", value ? "true" : "false")
}

const setTaskTargetDate = (value: string) => {
  localStorage.setItem("task_target_date", value)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTaskLabelSuggest = (value: LabelSuggest[]) => {
  localStorage.setItem("task_label_suggest", JSON.stringify(value))
}

const setInformationConfirmed = (value: number[]) => {
  localStorage.setItem("information_confirmed", value.join(","))
}

const setTaskStatusListView = (value: boolean) => {
  localStorage.setItem("task_status_list_view", value ? "true" : "false")
}

const setSummaryRangeType = (type: SummaryRangeType) => {
  localStorage.setItem("summary_range_type", type)
  localStorage.setItem("expiration_date", endOfDay().toString())
}

const setTaskApplication = (value: boolean) => {
  localStorage.setItem("task_application", value ? "true" : "false")
}

const setSideOpen = (value: boolean) => {
  localStorage.setItem("side_open", value ? "true" : "false")
}

const setTaskGanttChartDisplayTypeInit = (value: string) => {
  localStorage.setItem("task_gantt_chart_display_type_init", value)
}

const setTaskGanttChartRangeTypeInit = (value: string) => {
  localStorage.setItem("task_gantt_chart_range_type_init", value)
}

const setSettingHolidays = (value: boolean) => {
  localStorage.setItem("setting_holidays", value ? "true" : "false")
}

const setTaskSearchQuery = (value: string) => {
  localStorage.setItem("task_search_query", value)
}

const setTaskLabelSearchQuery = (value: string) => {
  localStorage.setItem("task_label_search_query", value)
}

const setTaskAutoLabelingSearchQuery = (value: string) => {
  localStorage.setItem("task_auto_labeling_search_query", value)
}

const setTaskTargetSettingDisplayType = (value: string) => {
  localStorage.setItem("task_target_setting_display_type", value)
}

const setTaskLabelSGroupOrder = (value: LabelGroupList[]) => {
  localStorage.setItem("task_label_group_order", JSON.stringify(value))
}

const setTaskLabelSettingCategoryView = (value: boolean) => {
  localStorage.setItem("task_label_setting_category_view", value ? "true" : "false")
}

const setTaskLabelSGroupLabelOrder = (value: LabelGroupLabelList[]) => {
  localStorage.setItem("task_label_group_label_order", JSON.stringify(value))
}

const setTaskLabelingBaseWorkTimeView = (value: boolean) => {
  localStorage.setItem("task_labeling_base_work_time_view", value ? "true" : "false")
}

const setTaskLabelingColumnNumber = (value: number) => {
  localStorage.setItem("task_labeling_column_number", value.toString())
}

const setTaskLabelingSortType = (value: string) => {
  localStorage.setItem("task_labeling_sort_type", value)
}

const setTaskLabelingGuide = (value: boolean) => {
  localStorage.setItem("task_labeling_guide", value ? "true" : "false")
}

const setTaskApprovalDisplayType = (value: string[]) => {
  localStorage.setItem("task_approval_display_type", JSON.stringify(value))
}

const setTaskApprovalStatusDisplayRate = (value: boolean) => {
  localStorage.setItem("task_approval_status_display_rate", value ? "true" : "false")
}

const setTaskLabelingDisplayBandChart = (value: boolean) => {
  localStorage.setItem("task_labeling_display_band_chart", value ? "true" : "false")
}

const setSettingTeamCreateTeam = (value: boolean) => {
  localStorage.setItem("setting_team_create_team", value ? "true" : "false")
}

const setSettingTeamOpenTeamIds = (value: number[]) => {
  localStorage.setItem("setting_team_open_team_ids", value.join(","))
}

const currentSlice = createSlice({
  name: 'current',
  initialState,
  reducers: {
    updateCurrentDate: (state: CurrentState, action: PayloadAction<string>) => {
      state.targetDate = format(action.payload, FORMAT_DAILY)
      setTargetDate(state.targetDate)
      return state
    },
    updateCurrentMonth: (state: CurrentState, action: PayloadAction<string>) => {
      state.targetMonth = action.payload
      setTargetMonth(state.targetMonth)
      return state
    },
    updateCurrentStartDate: (state: CurrentState, action: PayloadAction<string>) => {
      state.targetStartDate = action.payload
      setTargetStartDate(state.targetStartDate)
      return state
    },
    updateCurrentEndDate: (state: CurrentState, action: PayloadAction<string>) => {
      state.targetEndDate = action.payload
      setTargetEndDate(state.targetEndDate)
      return state
    },
    updateCurrentDashboardRangeType: (state: CurrentState, action: PayloadAction<string>) => {
      state.dashboardRangeType = action.payload as DashboardRangeType
      setDashboardRangeType(state.dashboardRangeType as DashboardRangeType)
      return state
    },
    updateCurrentReportRangeType: (state: CurrentState, action: PayloadAction<string>) => {
      state.reportRangeType = action.payload as ReportRangeType
      setReportRangeType(state.reportRangeType as ReportRangeType)
      return state
    },
    updateCurrentDashboardSelectTeam: (state: CurrentState, action: PayloadAction<string>) => {
      state.dashboardSelectTeam = action.payload
      setDashboardSelectTeam(state.dashboardSelectTeam)
      return state
    },
    updateCurrentDashboardSelectOccupation: (state: CurrentState, action: PayloadAction<string>) => {
      state.dashboardSelectOccupation = action.payload
      setDashboardSelectOccupation(state.dashboardSelectOccupation)
      return state
    },
    updateCurrentDashboardSelectContract: (state: CurrentState, action: PayloadAction<string>) => {
      state.dashboardSelectContract = action.payload
      setDashboardSelectContract(state.dashboardSelectContract)
      return state
    },
    updateCurrentDashboardSelectPlace: (state: CurrentState, action: PayloadAction<string>) => {
      state.dashboardSelectPlace = action.payload
      setDashboardSelectPlace(state.dashboardSelectPlace)
      return state
    },
    updateCurrentTimelineSelectTeam: (state: CurrentState, action: PayloadAction<string>) => {
      state.timelineSelectTeam = action.payload
      setTimelineSelectTeam(state.timelineSelectTeam)
      return state
    },
    updateCurrentTimelineSelectOccupation: (state: CurrentState, action: PayloadAction<string>) => {
      state.timelineSelectOccupation = action.payload
      setTimelineSelectOccupation(state.timelineSelectOccupation)
      return state
    },
    updateCurrentTimelineSelectContract: (state: CurrentState, action: PayloadAction<string>) => {
      state.timelineSelectContract = action.payload
      setTimelineSelectContract(state.timelineSelectContract)
      return state
    },
    updateCurrentTimelineSelectPlace: (state: CurrentState, action: PayloadAction<string>) => {
      state.timelineSelectPlace = action.payload
      setTimelineSelectPlace(state.timelineSelectPlace)
      return state
    },
    updateCurrentAnalyzeSelectDate: (state: CurrentState, action: PayloadAction<string>) => {
      state.analyzeSelectDate = action.payload
      setAnalyzeSelectDate(state.analyzeSelectDate)
      return state
    },
    updateCurrentAnalyzeSelectTeam: (state: CurrentState, action: PayloadAction<string>) => {
      state.analyzeSelectTeam = action.payload
      setAnalyzeSelectTeam(state.analyzeSelectTeam)
      return state
    },
    updateCurrentAnalyzeSelectMember: (state: CurrentState, action: PayloadAction<string>) => {
      state.analyzeSelectMember = action.payload
      setAnalyzeSelectMember(state.analyzeSelectMember)
      return state
    },
    updateTaskAnnounce: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskAnnounce = action.payload
      setTaskAnnounce(state.taskAnnounce)
      return state
    },
    updateTaskTargetDate: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskTargetDate = action.payload
      setTaskTargetDate(state.taskTargetDate)
      return state
    },
    updateTaskLabelSuggest: (state: CurrentState, action: PayloadAction<LabelSuggest[]>) => {
      state.taskLabelSuggest = action.payload
      setTaskLabelSuggest(state.taskLabelSuggest)
      return state
    },
    updateInformationConfirmed: (state: CurrentState, action: PayloadAction<number[]>) => {
      state.informationConfirmed = action.payload
      setInformationConfirmed(state.informationConfirmed)
      return state
    },
    updateTaskStatusListView: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskStatusListView = action.payload
      setTaskStatusListView(state.taskStatusListView)
      return state
    },
    updateSummaryRangeType: (state: CurrentState, action: PayloadAction<string>) => {
      state.summaryRangeType = action.payload as SummaryRangeType
      setSummaryRangeType(state.summaryRangeType as SummaryRangeType)
      return state
    },
    updateTaskApplication: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskApplication = action.payload
      setTaskApplication(state.taskApplication)
      return state
    },
    updateSideOpen: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.sideOpen = action.payload
      setSideOpen(state.sideOpen)
      return state
    },
    updateTaskGanttChartDisplayTypeInit: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskGanttChartDisplayTypeInit = action.payload
      setTaskGanttChartDisplayTypeInit(state.taskGanttChartDisplayTypeInit)
      return state
    },
    updateTaskGanttChartRangeTypeInit: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskGanttChartRangeTypeInit = action.payload
      setTaskGanttChartRangeTypeInit(state.taskGanttChartRangeTypeInit)
      return state
    },
    updateSettingHolidays: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.settingHolidays = action.payload
      setSettingHolidays(state.settingHolidays)
      return state
    },
    updateTaskSearchQuery: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskSearchQuery = action.payload
      setTaskSearchQuery(state.taskSearchQuery)
      return state
    },
    updateTaskLabelSearchQuery: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskLabelSearchQuery = action.payload
      setTaskLabelSearchQuery(state.taskLabelSearchQuery)
      return state
    },
    updateTaskAutoLabelingSearchQuery: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskAutoLabelingSearchQuery = action.payload
      setTaskAutoLabelingSearchQuery(state.taskAutoLabelingSearchQuery)
      return state
    },
    updateTaskTargetSettingDisplayType: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskTargetSettingDisplayType = action.payload
      setTaskTargetSettingDisplayType(state.taskTargetSettingDisplayType)
      return state
    },
    updateTaskLabelGroupOrder: (state: CurrentState, action: PayloadAction<LabelGroupList[]>) => {
      state.taskLabelGroupOrder = action.payload
      setTaskLabelSGroupOrder(state.taskLabelGroupOrder)
      return state
    },
    updateTaskLabelSettingCategoryView: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskLabelSettingCategoryView = action.payload
      setTaskLabelSettingCategoryView(state.taskLabelSettingCategoryView)
      return state
    },
    updateTaskLabelGroupLabelOrder: (state: CurrentState, action: PayloadAction<LabelGroupLabelList[]>) => {
      state.taskLabelGroupLabelOrder = action.payload
      setTaskLabelSGroupLabelOrder(state.taskLabelGroupLabelOrder)
      return state
    },
    updateTaskLabelingBaseWorkTimeView: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskLabelingBaseWorkTimeView = action.payload
      setTaskLabelingBaseWorkTimeView(state.taskLabelingBaseWorkTimeView)
      return state
    },
    updateTaskLabelingColumnNumber: (state: CurrentState, action: PayloadAction<number>) => {
      state.taskLabelingColumnNumber = action.payload
      setTaskLabelingColumnNumber(state.taskLabelingColumnNumber)
      return state
    },
    updateTaskLabelingSortType: (state: CurrentState, action: PayloadAction<string>) => {
      state.taskLabelingSortType = action.payload
      setTaskLabelingSortType(state.taskLabelingSortType)
      return state
    },
    updateTaskLabelingGuide: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskLabelingGuide = action.payload
      setTaskLabelingGuide(state.taskLabelingGuide)
      return state
    },
    updateTaskApprovalDisplayType: (state: CurrentState, action: PayloadAction<string[]>) => {
      state.taskApprovalDisplayType = action.payload
      setTaskApprovalDisplayType(state.taskApprovalDisplayType)
      return state
    },
    updateTaskApprovalStatusDisplayRate: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskApprovalStatusDisplayRate = action.payload
      setTaskApprovalStatusDisplayRate(state.taskApprovalStatusDisplayRate)
      return state
    },
    updateTaskLabelingDisplayBandChart: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.taskLabelingDisplayBandChart = action.payload
      setTaskLabelingDisplayBandChart(state.taskLabelingDisplayBandChart)
      return state
    },
    updateSettingTeamCreateTeam: (state: CurrentState, action: PayloadAction<boolean>) => {
      state.settingTeamCreateTeam = action.payload
      setSettingTeamCreateTeam(state.settingTeamCreateTeam)
      return state
    },
    updateSettingTeamOpenTeamIds: (state: CurrentState, action: PayloadAction<number[]>) => {
      state.settingTeamOpenTeamIds = action.payload
      setSettingTeamOpenTeamIds(state.settingTeamOpenTeamIds)
      return state
    },
  },
})

export const {
  updateCurrentDate,
  updateCurrentMonth,
  updateCurrentStartDate,
  updateCurrentEndDate,
  updateCurrentDashboardRangeType,
  updateCurrentReportRangeType,
  updateCurrentDashboardSelectTeam,
  updateCurrentDashboardSelectOccupation,
  updateCurrentDashboardSelectContract,
  updateCurrentDashboardSelectPlace,
  updateCurrentTimelineSelectTeam,
  updateCurrentTimelineSelectOccupation,
  updateCurrentTimelineSelectContract,
  updateCurrentTimelineSelectPlace,
  updateCurrentAnalyzeSelectDate,
  updateCurrentAnalyzeSelectTeam,
  updateCurrentAnalyzeSelectMember,
  updateTaskAnnounce,
  updateTaskTargetDate,
  updateTaskLabelSuggest,
  updateInformationConfirmed,
  updateTaskStatusListView,
  updateSummaryRangeType,
  updateTaskApplication,
  updateSideOpen,
  updateTaskGanttChartDisplayTypeInit,
  updateTaskGanttChartRangeTypeInit,
  updateSettingHolidays,
  updateTaskSearchQuery,
  updateTaskLabelSearchQuery,
  updateTaskAutoLabelingSearchQuery,
  updateTaskTargetSettingDisplayType,
  updateTaskLabelGroupOrder,
  updateTaskLabelSettingCategoryView,
  updateTaskLabelGroupLabelOrder,
  updateTaskLabelingBaseWorkTimeView,
  updateTaskLabelingColumnNumber,
  updateTaskLabelingSortType,
  updateTaskLabelingGuide,
  updateTaskApprovalDisplayType,
  updateTaskApprovalStatusDisplayRate,
  updateTaskLabelingDisplayBandChart,
  updateSettingTeamCreateTeam,
  updateSettingTeamOpenTeamIds,
} = currentSlice.actions

export const currentReducer = currentSlice.reducer
